import { useState, useCallback, useEffect, useRef } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { TransitionProvider } from './context/TransitionContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import TransitionOverlay from './components/TransitionOverlay';
import CursorBubble from './components/CursorBubble';
import Preloader from './components/Preloader';
import ErrorBoundary from './components/ErrorBoundary';
import Sidebar from './layout/Sidebar';
import NewLayout from './layout/NewLayout';
import Home from './home/Home';
import About from './about/About';
import Work from './work/Work';
import Contact from './contact/Contact';
import Pricing from './pricing/Pricing';
import SellerHome from './pages/SellerHome';
import SellerHelp from './pages/SellerHelp';
import ProjectDetail from './pages/ProjectDetail';
import LegalPage from './pages/LegalPage';
import NotFound from './components/NotFound';
import { getProjectFromSubdomain } from './utils/subdomain';
import AnimatedButton from './components/AnimatedButton';
import { AuthProvider, useAuth } from './context/AuthContext';
import Dashboard from './portal/Dashboard';
import PaymentReturn from './portal/PaymentReturn';
import ProtectedRoute from './portal/ProtectedRoute';

gsap.registerPlugin(ScrollTrigger);

// Page enter/exit variants.
// IMPORTANT: opacity-only (no x/y/scale). A transform on this wrapper — even
// translateY(0) at rest — makes it a containing block, which breaks position:fixed
// descendants (the corner avatar vanished) AND ScrollTrigger pinning (HorizontalWords
// jumped instantly). Opacity doesn't create a containing block, so both work.
const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.4, delay: 0.6, ease: 'easeOut' } },
  exit: { opacity: 0, transition: { duration: 0.3, ease: 'easeIn' } },
};

// All fixed UI elements — rendered OUTSIDE motion.div so position:fixed works
function FixedUI() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isDarkTheme } = useTheme();
  const { user } = useAuth();
  const [use24Hour, setUse24Hour] = useState(false);
  const [timeStr, setTimeStr] = useState({ time: '', period: '' });
  const progressDotRef = useRef(null);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      if (use24Hour) {
        const time = now.toLocaleTimeString('id-ID', { timeZone: 'Asia/Jakarta', hour: '2-digit', minute: '2-digit' }).replace('.', ':');
        setTimeStr({ time, period: '' });
      } else {
        const time = now.toLocaleTimeString('en-US', { timeZone: 'Asia/Jakarta', hour: 'numeric', minute: '2-digit', hour12: true });
        const parts = time.split(' ');
        setTimeStr({ time: parts[0], period: parts[1] || '' });
      }
    };
    updateTime();
    const interval = setInterval(updateTime, 5000);
    return () => clearInterval(interval);
  }, [use24Hour]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(progressDotRef.current, {
        y: 184,
        ease: 'none',
        scrollTrigger: {
          trigger: document.body,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.2,
        },
      });
    });
    return () => ctx.revert();
  }, []);

  // Hide fixed UI on seller pages
  if (location.pathname.startsWith('/seller')) return null;

  // On the client portal, keep the sidebar nav but drop the marketing chrome
  // (book-a-call, scroll progress, clock) so it doesn't overlap the dashboard.
  const isPortal = location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/payment');

  return (
    <>
      <Sidebar isDarkTheme={isDarkTheme} />

      {!isPortal && (
      <>
      {/* Top Right Book a Call Button */}
      <div style={{ position: 'fixed', top: '32px', right: '40px', zIndex: 100 }}>
        <AnimatedButton
          onClick={() => navigate(user ? '/dashboard' : '/contact')}
          style={{
            backgroundColor: isDarkTheme ? 'white' : 'rgba(23, 20, 18, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            color: isDarkTheme ? 'black' : 'white',
            border: isDarkTheme ? 'none' : '1px solid rgba(255,255,255,0.15)',
            borderRadius: '8px',
            padding: '9px 18px',
            fontWeight: 800,
            fontSize: '12.5px',
            WebkitTextStroke: '0.5px currentColor',
            letterSpacing: '0.05em',
            boxShadow: isDarkTheme ? 'none' : '0 4px 12px rgba(0,0,0,0.1)',
            fontFamily: 'var(--v2-font-mono)',
          }}
          bgColor="transparent" /* let inline style handle bg to support backdrop filter */
          textColor="inherit"
          hoverBgColor="#673df2"
          hoverTextColor="#ffffff"
        >
          {user ? `Alo ${user.user_metadata?.full_name?.split(' ')[0] || user.email?.split('@')[0] || ''}, dashboard` : t('home.hireSoon')}
        </AnimatedButton>
      </div>

      {/* Scroll Progress Bar */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          right: '40px',
          transform: 'translateY(-50%)',
          height: '200px',
          width: '40px',
          zIndex: 100,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <div
          ref={progressDotRef}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '16px' }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              marginLeft: '-0.5px',
              marginBottom: '6px',
              width: '1px',
              height: '200px',
              backgroundColor: isDarkTheme ? 'rgba(255,255,255,0.2)' : 'rgba(23,20,18,0.15)',
              transition: 'background-color 0.3s ease',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '4px',
              height: '4px',
              backgroundColor: isDarkTheme ? 'white' : '#171412',
              borderRadius: '50%',
              transition: 'background-color 0.3s ease',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '-4px',
                left: '-4px',
                right: '-4px',
                bottom: '-4px',
                border: isDarkTheme ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(23,20,18,0.15)',
                borderRadius: '50%',
                transition: 'border-color 0.3s ease',
              }}
            />
          </div>
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              marginLeft: '-0.5px',
              marginTop: '6px',
              width: '1px',
              height: '200px',
              backgroundColor: isDarkTheme ? 'rgba(255,255,255,0.2)' : 'rgba(23,20,18,0.15)',
              transition: 'background-color 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* Bottom Right Location & Time */}
      <div
        onClick={() => setUse24Hour(!use24Hour)}
        style={{
          position: 'fixed',
          bottom: '32px',
          right: '56px',
          zIndex: 100,
          fontSize: '12px',
          fontWeight: 500,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span style={{ color: '#9b9a99', transition: 'color 0.3s ease' }}>Semarang, Indonesia </span>
        <span style={{ color: isDarkTheme ? 'white' : '#171412', transition: 'color 0.3s ease' }}>{timeStr.time} </span>
        {timeStr.period && <span style={{ color: '#9b9a99', transition: 'color 0.3s ease' }}>{timeStr.period}</span>}
      </div>
      </>
      )}
    </>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const projectSubdomain = getProjectFromSubdomain();

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{ minHeight: '100%' }}
      >
        <Routes location={location}>
          <Route
            path="/"
            element={
              <NewLayout>
                {projectSubdomain ? <ProjectDetail slug={projectSubdomain} /> : <Home />}
              </NewLayout>
            }
          />
          <Route path="/about" element={<NewLayout><About /></NewLayout>} />
          <Route path="/work" element={<NewLayout><Work /></NewLayout>} />
          <Route path="/pricing" element={<NewLayout><Pricing /></NewLayout>} />
          <Route path="/dashboard" element={<NewLayout><ProtectedRoute><Dashboard /></ProtectedRoute></NewLayout>} />
          <Route path="/payment/return" element={<NewLayout><PaymentReturn /></NewLayout>} />
          <Route path="/work/:slug" element={<NewLayout><ProjectDetail /></NewLayout>} />
          <Route path="/contact" element={<NewLayout><Contact /></NewLayout>} />
          <Route path="/privacy" element={<NewLayout><LegalPage kind="privacy" /></NewLayout>} />
          <Route path="/terms" element={<NewLayout><LegalPage kind="terms" /></NewLayout>} />
          <Route path="/seller" element={<SellerHome />} />
          <Route path="/seller/help" element={<SellerHelp />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  const [isLoading, setIsLoading] = useState(true);

  const handlePreloaderComplete = useCallback(() => {
    setIsLoading(false);
  }, []);

  return (
    <LanguageProvider>
      {isLoading && <Preloader onComplete={handlePreloaderComplete} />}
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <TransitionProvider>
              <ErrorBoundary>
                <TransitionOverlay />
                <CursorBubble />
                <FixedUI />
                <AnimatedRoutes />
              </ErrorBoundary>
            </TransitionProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
